import { css } from '@styled-system/css';
import { ArrowUpRight, Clock, Heart, Play } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VestiaireLockedButton } from '@/shared/components/analysis/VestiaireLockedChip';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Button } from '@/shared/components/ui/Button';
import { ImageLightbox } from '@/shared/components/ui/ImageLightbox';
import { Tag, type TagKind } from '@/shared/components/ui/Tag';
import i18n from '@/shared/i18n';
import type { ProductItemData } from '@/shared/types/product';
import { getItemConditionLabel } from '@/shared/utils/analysis/itemCondition';
import { getTimeAgo } from '@/shared/utils/getTimeAgo';
import { type ConditionTier, getConditionTierFromCondition } from '@/shared/utils/vinted-labels';

// ============================================================================
// Carte produit — adaptation LECTURE SEULE de l'ItemFeed d'Autocop-refonte
// (sans machine d'achat, sans pill de filtre, sans traduction). Partagée
// entre l'historique des ventes produit et la future analyse vendeur.
// ============================================================================

const conditionTierToKind: Record<ConditionTier, TagKind> = {
  excellent: 'conditionExcellent',
  good: 'conditionGood',
  fair: 'conditionFair',
  poor: 'conditionPoor',
};

const formatPriceMono = (amount: number | null | undefined) =>
  amount == null ? '—' : `${amount.toFixed(2).replace('.', ',')} €`;

/** Durée entre mise en ligne et vente — "3j 4h" / "5h". */
const formatSaleDuration = (uploadedAt: string, soldAt: string): string | null => {
  const hours = (new Date(soldAt).getTime() - new Date(uploadedAt).getTime()) / 3_600_000;
  if (!Number.isFinite(hours) || hours < 0) return null;
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours % 24);
  if (days === 0) return `${Math.max(rest, 1)}h`;
  const dayUnit = i18n.t('ui.timeUnits.day');
  return rest > 0 ? `${days}${dayUnit} ${rest}h` : `${days}${dayUnit}`;
};

// ─── Styles (repris d'ItemFeed, épurés) ────────────────────────────────────

const card = css({
  position: 'relative',
  display: 'flex',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: '2xl',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
  height: '300px',
  width: '100%',
  containerType: 'inline-size',
  containerName: 'product-card',
  _hover: {
    borderColor: 'border.strong',
    boxShadow: 'md',
    transform: 'translateY(-1px)',
  },
});

const compactLayoutWrap = css({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  '@container product-card (min-width: 800px)': { display: 'none' },
});

const wideLayoutWrap = css({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'none',
  '@container product-card (min-width: 800px)': {
    display: 'flex',
    flexDirection: 'column',
  },
});

const imageColumn = css({
  position: 'relative',
  width: '200px',
  flexShrink: 0,
  bg: 'surface.subtle',
  borderRightWidth: '1px',
  borderRightStyle: 'solid',
  borderRightColor: 'border.default',
  cursor: 'zoom-in',
  overflow: 'hidden',
});

const mainImage = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 200ms ease-out',
  willChange: 'transform',
});

const favBadge = css({
  position: 'absolute',
  top: '5',
  right: '5',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1.5',
  bg: 'rgba(0, 0, 0, 0.55)',
  color: 'white',
  borderRadius: 'full',
  paddingX: '3',
  paddingY: '1',
  fontSize: 'micro',
  fontWeight: 'semibold',
  fontFamily: 'mono',
  pointerEvents: 'none',
});

const thumbStrip = css({
  position: 'absolute',
  bottom: '6',
  left: '6',
  right: '6',
  display: 'flex',
  gap: '3',
});

const thumb = css({
  width: '42px',
  height: '42px',
  borderRadius: 'md',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'opacity 120ms ease, transform 120ms ease',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'rgba(255,255,255,0.35)',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  _hover: { opacity: 1 },
});

const thumbImg = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const compactBody = css({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  paddingY: '6',
  paddingX: '6',
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
  '@container product-card (max-width: 399.98px)': { paddingX: '3', paddingY: '4', gap: '2' },
});

const wideBody = css({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  paddingY: '8',
  paddingX: '10',
  display: 'flex',
  flexDirection: 'column',
  gap: '6',
});

const wideTopRow = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '6',
});

const wideTitleColumn = css({ flex: 1, minWidth: 0 });

const wideTitleRow = css({ display: 'block', marginBottom: '4' });

const wideTitleLink = css({
  fontSize: 'h3',
  lineHeight: '1.3',
  fontWeight: 'semibold',
  color: 'text.primary',
  letterSpacing: '-0.25px',
  textDecoration: 'none',
  display: 'inline',
  transition: 'color 120ms ease',
  wordBreak: 'break-word',
  _hover: { color: 'brand.primary' },
});

const widePriceColumn = css({
  textAlign: 'right',
  flexShrink: 0,
});

const widePriceMain = css({
  fontFamily: 'mono',
  fontSize: '26px',
  fontWeight: 'bold',
  lineHeight: '1',
  color: 'text.primary',
  letterSpacing: '-0.8px',
});

const soldChip = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  paddingX: '3',
  paddingY: '1',
  borderRadius: 'full',
  bg: 'success.soft',
  color: 'success.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'success.border',
  fontSize: 'caption',
  fontWeight: 'medium',
  whiteSpace: 'nowrap',
});

const durationChip = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1.5',
  paddingX: '3',
  paddingY: '1',
  borderRadius: 'full',
  bg: 'surface.sunken',
  color: 'text.secondary',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  fontSize: 'caption',
  fontFamily: 'mono',
  fontWeight: 'medium',
  whiteSpace: 'nowrap',
});

const soldChipsRow = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  marginTop: '3',
});

const titleRow = css({ display: 'block' });

const titleLink = css({
  fontSize: 'body',
  lineHeight: '1.3',
  fontWeight: 'semibold',
  color: 'text.primary',
  letterSpacing: '-0.2px',
  textDecoration: 'none',
  display: 'inline',
  transition: 'color 120ms ease',
  wordBreak: 'break-word',
  _hover: { color: 'brand.primary' },
});

const tagRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '2',
  flexWrap: 'wrap',
});

const priceMain = css({
  fontFamily: 'mono',
  fontSize: '20px',
  fontWeight: 'bold',
  lineHeight: '1',
  color: 'text.primary',
  letterSpacing: '-0.5px',
});

const actionsRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  flexWrap: 'wrap',
  marginTop: '2',
});

const descriptionWrapper = css({
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  position: 'relative',
  '@container product-card (max-width: 399.98px)': { display: 'none' },
});

const descriptionText = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  lineHeight: '1.45',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontStyle: 'italic',
  borderLeftWidth: '2px',
  borderLeftStyle: 'solid',
  borderLeftColor: 'border.subtle',
  paddingLeft: '3',
  paddingTop: '1',
  height: '100%',
  maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 28px), transparent 100%)',
  WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 28px), transparent 100%)',
});

const sellerRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  paddingTop: '4',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'border.subtle',
  minWidth: 0,
});

const sellerInfo = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  minWidth: 0,
  flex: 1,
  paddingX: '2',
  paddingY: '1',
  marginX: '-2',
  marginY: '-1',
  borderRadius: 'md',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background 120ms ease',
  _hover: {
    bg: 'surface.sunken',
    '& .seller-name': { color: 'brand.primary' },
  },
  '@container product-card (max-width: 399.98px)': { display: 'none' },
});

const sellerStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5',
  minWidth: 0,
});

const sellerName = css({
  fontSize: 'caption',
  fontWeight: 'medium',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  transition: 'color 120ms ease',
});

const sellerMeta = css({
  fontSize: 'micro',
  fontFamily: 'mono',
  color: 'text.tertiary',
});

const wideSellerRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '6',
  paddingTop: '8',
  paddingBottom: '5',
  minHeight: '64px',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'border.subtle',
});

const wideSellerInfo = css({
  display: 'flex',
  alignItems: 'center',
  gap: '5',
  minWidth: 0,
  paddingX: '3',
  paddingY: '2',
  marginX: '-3',
  marginY: '-2',
  borderRadius: 'md',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background 120ms ease',
  _hover: {
    bg: 'surface.sunken',
    '& .seller-name': { color: 'brand.primary' },
  },
});

const wideSellerName = css({
  fontSize: 'bodySm',
  fontWeight: 'medium',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '160px',
  transition: 'color 120ms ease',
});

const wideVerticalSep = css({
  width: '1px',
  height: '24px',
  bg: 'border.default',
});

const wideTimestamp = css({
  fontSize: 'micro',
  fontFamily: 'mono',
  color: 'text.tertiary',
  marginLeft: 'auto',
  flexShrink: 0,
});

// ============================================================================
// Component
// ============================================================================

export interface ProductFeedCardProps {
  product: ProductItemData;
  onProductClick?: (product: ProductItemData) => void;
  /** Lance une recherche sur les articles similaires (bouton discret). */
  onSearchSimilar?: (product: ProductItemData) => void;
  /**
   * « Articles similaires » verrouillé par le plan (analyse Vestiaire sans accès VC) : grisé,
   * cadenas, infobulle d'abonnement ; le clic n'appelle jamais `onSearchSimilar`, il affiche la
   * notification d'abonnement (`VestiaireLockedButton`).
   */
  similarLocked?: boolean;
}

export const ProductFeedCard: FC<ProductFeedCardProps> = ({
  product,
  onProductClick,
  onSearchSimilar,
  similarLocked = false,
}) => {
  const { t, i18n } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState<{ x: number; y: number } | null>(null);

  const lang = i18n.language ?? 'fr';
  const conditionLabel = product.itemCondition ? getItemConditionLabel(product.itemCondition) : null;
  // const size = product.attributes.find((a) => a.name.toLowerCase() === 'taille')?.value;
  const category = product.attributes.find((a) => a.name.toLowerCase() === 'catégorie')?.value;
  const saleDuration = product.soldAt ? formatSaleDuration(product.uploadedAt, product.soldAt) : null;
  // `sellerRating` est le `feedback_reputation` brut de Vinted, sur l'échelle 0–1 (et non 0–5).
  const ratingPercent = product.sellerRating != null ? Math.round(product.sellerRating * 100) : null;
  const referenceDate = product.soldAt ?? product.uploadedAt;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(lang.startsWith('en') ? 'en-GB' : 'fr-FR');

  // Clic sur la carte : comportement par défaut = ouvrir l'annonce Vinted
  // dans un nouvel onglet (surchargez via onProductClick si besoin).
  const handleCardClick = () => {
    if (onProductClick) {
      onProductClick(product);
    } else {
      window.open(product.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.images.length === 0) return;
    setLightboxOpen(true);
  };

  const handleImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setZoomOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const visibleThumbs = product.images.slice(0, 4);
  const hasMultipleImages = visibleThumbs.length > 1;

  const tagsEl = (
    <>
      {category && <Tag kind="category">{category}</Tag>}
      {product.brand && <Tag kind="brand">{product.brand}</Tag>}
      {/* Tag taille désactivé pour libérer de l'espace — à réactiver si besoin.
      {size && <Tag kind="size">{size}</Tag>} */}
      {conditionLabel &&
        (() => {
          const tier = product.itemCondition ? getConditionTierFromCondition(product.itemCondition) : null;
          const kind: TagKind = tier ? conditionTierToKind[tier] : 'condition';
          return <Tag kind={kind}>{conditionLabel}</Tag>;
        })()}
    </>
  );

  const soldChipsEl = product.soldAt ? (
    <span className={soldChipsRow}>
      <span className={soldChip}>{t('product.soldOn', { date: formatDate(product.soldAt) })}</span>
      {saleDuration && (
        <span className={durationChip} title={t('salesFeed.soldIn', { duration: saleDuration })}>
          <Clock size={11} strokeWidth={1.75} />
          {saleDuration}
        </span>
      )}
    </span>
  ) : null;

  const similarBtnEl = onSearchSimilar ? (
    similarLocked ? (
      <VestiaireLockedButton variant="secondary" size="md" iconSize={13}>
        {t('salesFeed.similar')}
      </VestiaireLockedButton>
    ) : (
      <Button
        variant="secondary"
        size="md"
        icon={<Play size={13} strokeWidth={1.75} />}
        onClick={(e) => {
          e.stopPropagation();
          onSearchSimilar(product);
        }}
        title={t('salesFeed.similarTitle')}
      >
        {t('salesFeed.similar')}
      </Button>
    )
  ) : null;

  const titleEl = (wide: boolean) => (
    <a
      href={product.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={wide ? wideTitleLink : titleLink}
    >
      {product.title}{' '}
      <ArrowUpRight
        size={14}
        strokeWidth={1.75}
        style={{ display: 'inline', verticalAlign: 'middle', flexShrink: 0 }}
      />
    </a>
  );

  const sellerEl = (wide: boolean) => (
    <a
      href={product.sellerUrl ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      className={wide ? wideSellerInfo : sellerInfo}
      onClick={(e) => {
        if (!product.sellerUrl) e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Avatar name={product.sellerName || '?'} size="sm" />
      <div className={wide ? css({ display: 'flex', flexDirection: 'column' }) : sellerStack}>
        <span className={`${wide ? wideSellerName : sellerName} seller-name`}>@{product.sellerName || '—'}</span>
        {product.sellerReviews != null && product.sellerReviews > 0 ? (
          <span className={sellerMeta}>
            {ratingPercent != null && (
              <>
                <span className={css({ color: 'success.default', fontWeight: 'semibold' })}>{ratingPercent}%</span>
                {' · '}
              </>
            )}
            {t('salesFeed.reviews', { count: product.sellerReviews })}
          </span>
        ) : (
          <span className={sellerMeta}>{t('salesFeed.noReviews')}</span>
        )}
      </div>
    </a>
  );

  const descriptionEl = product.description ? (
    <div className={descriptionWrapper}>
      <div className={descriptionText}>{product.description}</div>
    </div>
  ) : (
    <div className={css({ flex: 1 })} />
  );

  return (
    <article className={card} onClick={handleCardClick}>
      {/* Colonne image — loupe hover + vignettes + lightbox */}
      <div
        className={imageColumn}
        onClick={handleImageClick}
        onMouseMove={handleImageMouseMove}
        onMouseLeave={() => setZoomOrigin(null)}
        role="button"
        tabIndex={-1}
        aria-label={t('salesFeed.openImage')}
      >
        {product.images[currentImageIndex] && (
          <img
            src={product.images[currentImageIndex]}
            alt={product.title}
            className={mainImage}
            style={
              zoomOrigin ? { transform: 'scale(3.2)', transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%` } : undefined
            }
          />
        )}

        {product.favouriteCount > 0 && (
          <span className={favBadge}>
            <Heart size={10} strokeWidth={2} />
            {product.favouriteCount}
          </span>
        )}

        {hasMultipleImages && (
          <div
            className={thumbStrip}
            onMouseEnter={() => setZoomOrigin(null)}
            onMouseMove={(e) => {
              e.stopPropagation();
              setZoomOrigin(null);
            }}
          >
            {visibleThumbs.map((src, index) => (
              <button
                key={`${src}-${index}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                onMouseEnter={() => setCurrentImageIndex(index)}
                className={thumb}
                style={{
                  background: 'transparent',
                  padding: 0,
                  borderColor: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.35)',
                  opacity: index === currentImageIndex ? 1 : 0.7,
                }}
              >
                <img src={src} alt="" className={thumbImg} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Compact layout — < 800px */}
      <div className={compactLayoutWrap}>
        <div className={compactBody}>
          <span className={titleRow}>{titleEl(false)}</span>
          <div className={tagRow}>{tagsEl}</div>
          <div className={css({ display: 'flex', alignItems: 'center', gap: '4', flexWrap: 'wrap' })}>
            <span className={priceMain}>{formatPriceMono(product.price)}</span>
            {soldChipsEl}
          </div>
          {similarBtnEl && <div className={actionsRow}>{similarBtnEl}</div>}
          {descriptionEl}
          <div className={sellerRow}>
            {sellerEl(false)}
            <span className={wideTimestamp}>{getTimeAgo(referenceDate, lang)}</span>
          </div>
        </div>
      </div>

      {/* Wide layout — ≥ 800px */}
      <div className={wideLayoutWrap}>
        <div className={wideBody}>
          <div className={wideTopRow}>
            <div className={wideTitleColumn}>
              <span className={wideTitleRow}>{titleEl(true)}</span>
              <div className={tagRow}>{tagsEl}</div>
            </div>
            <div className={widePriceColumn}>
              <div className={widePriceMain}>{formatPriceMono(product.price)}</div>
              {soldChipsEl}
            </div>
          </div>

          {descriptionEl}

          <div className={wideSellerRow}>
            {sellerEl(true)}
            <span className={wideVerticalSep} />
            <span className={wideTimestamp}>{getTimeAgo(referenceDate, lang)}</span>
            {similarBtnEl}
          </div>
        </div>
      </div>

      <ImageLightbox
        images={product.images}
        initialIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        alt={product.title}
      />
    </article>
  );
};
